import styled from 'styled-components';
import { Body3Medium, Body3SemiBold, Body4Medium, Body4Regular, Body5Regular, Title2SemiBold } from 'styles/Global/typography';
import AssessmentCard from './Components/AssessmentCard';
import TotalCard from './Components/TotalCard';
import { BackgroundColor, PrimaryColor, TextColor } from 'styles/Global/colorSemantic';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RecommendItem as UIRecommendItem, SummaryItem as UISummaryItem, TargetResult } from 'models/MindUp/MindUpTypes';
import RecommandCard from './Components/RecommandCard';
import DescriptionContainer from './Components/DescriptionContainer';
import { formatDateFlexible } from 'utils/calculateTime';
import SectionCard from './Components/SectionCard';
import { useGovernmentHeaderStore } from 'store/useGovernmentHeaderStore';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import AiRobot from 'assets/Icons/Ai/AiRobot';
import { connectMindPaths, preciousChildPaths } from 'constants/path';
import { ServiceButton, ServiceType } from './Components/ServiceButton';
import useToggle from 'hooks/CustomHooks/useToggle';
import MobileLeaveModal from 'components/Modal/MobileLeaveModal';
import { MODAL_CONTENT } from '../Sungnam/AskToAI/constants';
import _ from 'lodash';
import { DCKDiagnosisApi } from 'api/DCK/DCKDiagnosisApi';
import { applyPsyMeta } from 'utils/postProcessAiResult';
import { downloadNodeIfReady } from 'utils/captureImage';

const SAVE_BTN_ID = 'AI_ANALYZE_SAVE_BTN';
const filename = (name?: string | null) => `${name ?? '아이'}의 검사결과.png`;

function useHeaderSaveButton(setHeaderText: (v: React.ReactNode) => void, onSave: () => void) {
    useEffect(() => {
        setHeaderText(
            <HeaderWrapper>
                <HeaderTitleBox>
                    <Body3Medium>AI 종합 분석</Body3Medium>
                </HeaderTitleBox>
                <SaveButton id={SAVE_BTN_ID} role='button' tabIndex={0}>
                    <Body4Regular $color={TextColor.color_text_light}>저장</Body4Regular>
                </SaveButton>
            </HeaderWrapper>
        );

        const attach = () => {
            const btn = document.getElementById(SAVE_BTN_ID);
            if (!btn) return false;
            const click = (e: Event) => {
                e.preventDefault();
                onSave();
            };
            const key = (e: KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSave();
                }
            };
            btn.addEventListener('click', click);
            btn.addEventListener('keydown', key);
            return () => {
                btn.removeEventListener('click', click);
                btn.removeEventListener('keydown', key);
            };
        };

        let cleanup: (() => void) | void;
        const t = setTimeout(() => {
            cleanup = attach() || cleanup;
        }, 0);
        return () => {
            clearTimeout(t);
            if (typeof cleanup === 'function') cleanup();
        };
    }, [setHeaderText, onSave]);
}

const AIAnalyzePage = () => {
    const { state } = useLocation();
    const { setHeaderText } = useGovernmentHeaderStore();
    const [isShowEnterModal, setIsShowEnterModal] = useToggle();
    const navigate = useNavigate();

    const rawResult = state?.result;
    const diagnosisInfo = state?.data?.diagnosisInfo;
    const voucher = state?.data?.voucher;
    const token = state?.data?.token;
    const testRawData = state?.testRawData;

    const processed = useMemo(() => (rawResult ? applyPsyMeta(rawResult, testRawData) : { totalResult: null, summaryList: [], recommendList: [] as UIRecommendItem[] }), [rawResult, testRawData]);

    const totalResult = processed.totalResult;

    const summaryListSorted = useMemo(() => {
        const ui = toUiSummaryItems(processed.summaryList as unknown as Partial<UISummaryItem>[]);
        return sortByTargetPriority(ui.filter(isVisibleSummaryItem));
    }, [processed.summaryList]);

    const recommendList: UIRecommendItem[] = useMemo(() => (processed.recommendList ?? []) as UIRecommendItem[], [processed.recommendList]);

    const parsedDiagnosisDateTime = formatDateFlexible(diagnosisInfo?.diagnosisDateTime ?? '', 'YYYY/MM/DD');

    const captureRef = useRef<HTMLDivElement>(null);

    const onSave = useCallback(async () => {
        const node = captureRef.current;
        if (!node) return;
        const res = await downloadNodeIfReady(node, filename(diagnosisInfo?.patientName), {
            background: '#fff',
            timeoutMs: 15000,
        });
        if (!res.ok) {
            if (res.reason === 'images_not_ready') {
                alert('이미지 준비중입니다. 잠시만 기다려 주세요!');
            } else {
                alert('이미지 생성에 실패했어요. 잠시 후 다시 시도해주세요.');
            }
        }
    }, [diagnosisInfo?.patientName]);

    useHeaderSaveButton(setHeaderText, onSave);

    return (
        <PageLayout>
            <div ref={captureRef}>
                {isShowEnterModal && (
                    <MobileLeaveModal
                        title='이용 전 꼭 확인해주세요.'
                        firstButton='확인'
                        firstButtonType='fill'
                        infoContents={
                            <>
                                {_.map(MODAL_CONTENT.ENTER_MODAL.ITEMS, (item, index) => (
                                    <AiAskModalContainer key={index} $gap='6px'>
                                        <Body4Medium>-</Body4Medium>
                                        <div>
                                            <Body3Medium>{item.first}</Body3Medium>
                                            <Body3SemiBold>{item.medium}</Body3SemiBold>
                                            <Body3Medium>{item.last}</Body3Medium>
                                        </div>
                                    </AiAskModalContainer>
                                ))}
                            </>
                        }
                        setIsLeaveModal={async () => {
                            const fetched = await DCKDiagnosisApi.getDiagnosis(diagnosisInfo._id, token);
                            const latest = fetched?.data;
                            navigate(preciousChildPaths.sungnamAskToAi, {
                                state: {
                                    data: { diagnosisInfo: latest, voucher, token },
                                    analysisResult: { totalResult, summaryList: summaryListSorted, recommendList, diagnosisInfo },
                                    testRawData,
                                },
                            });
                        }}
                    />
                )}

                <SectionCard padding='0px 0px 32px 0px' title=''>
                    <div style={{ paddingLeft: '16px' }}>
                        <AiRobot width='52px' height='52px' />
                    </div>
                    <Title>
                        <Title2SemiBold>AI가 분석한</Title2SemiBold>
                        <Title2SemiBold>
                            <Title2SemiBold $color={PrimaryColor.color_primary_normal}>{diagnosisInfo?.patientName}</Title2SemiBold>의 검사 결과입니다.
                        </Title2SemiBold>
                    </Title>
                    <SubInfo>
                        <Body3Medium>{diagnosisInfo?.gradeName}</Body3Medium>
                        <Body5Regular $color={TextColor.color_text_light}>({parsedDiagnosisDateTime} 검사기준)</Body5Regular>
                    </SubInfo>
                    <NotiContainer>
                        <Body5Regular $color={TextColor.color_text_weak}>
                            *이 분석은 AI 기술을 활용한 예측 결과로, 실제 진단이나 전문적 판단을 대체하지 않습니다. 더 정확한 판단을 위해 전문가의 도움을 받는 걸 추천드려요!
                        </Body5Regular>
                    </NotiContainer>
                </SectionCard>

                <SectionCard title='종합 결과' backgroundColor={BackgroundColor.color_background_light}>
                    <ColumnList gap='12px' padding='0px 16px'>
                        {totalResult &&
                            totalResult?.targetResult?.map((item: TargetResult, index: number) => (
                                <TotalCard key={index} target={item.target} character={item.character} description={item.description} />
                            ))}
                    </ColumnList>
                    <DescriptionContainer description={totalResult?.conclusion} padding='20px 24px' />
                </SectionCard>

                <SectionCard title='검사별 요약'>
                    <ColumnList gap='32px' padding='0px'>
                        {summaryListSorted.map((item, index) => (
                            <AssessmentCard key={index} target={item.target} englishTitle={item.englishTitle} koreanTitle={item.koreanTitle} description={item.description} />
                        ))}
                    </ColumnList>
                </SectionCard>

                <SectionCard title='제안 및 추천' padding='48px 0px 100px 0px' backgroundColor={BackgroundColor.color_background_primary_neutral}>
                    <ColumnList gap='12px' padding='0px 16px'>
                        {recommendList?.map((item: UIRecommendItem, index: number) => (
                            <RecommandCard key={index} keyword={item.keyword} description={item.description} />
                        ))}
                    </ColumnList>
                </SectionCard>
            </div>

            <BottomButtonContainer>
                <ServiceButton type={ServiceType.AI_QUESTION} onClick={setIsShowEnterModal} />
                {!diagnosisInfo?.paymentId && (
                    <ServiceButton
                        type={ServiceType.COUNSEL_REQUEST}
                        onClick={() => {
                            navigate(connectMindPaths.sungnamPurchaseCounselService, {
                                state: { diagnosisInfo, voucher, token },
                            });
                        }}
                    />
                )}
            </BottomButtonContainer>
        </PageLayout>
    );
};

export default AIAnalyzePage;

const PageLayout = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;
const Title = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0px 16px;
    font-size: 24px;
`;
const SubInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 16px 20px 16px;
`;
const NotiContainer = styled.div`
    display: flex;
    gap: 4px;
    padding: 0px 16px;
`;
const ColumnList = styled.div<{ gap?: string; padding?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.gap ?? '12px'};
    padding: ${(props) => props.padding ?? '0'};
`;
const HeaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 60px;
    gap: 4px;
`;
const HeaderTitleBox = styled.div`
    min-width: 240px;
    position: absolute;
    display: flex;
    left: 50%;
    transform: translateX(-50%);
    justify-content: center;
    align-items: start;
`;
const SaveButton = styled.div`
    position: absolute;
    right: 0;
    min-width: 60px;
    height: 56px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    user-select: none;
`;
const BottomButtonContainer = styled.div`
    display: flex;
    padding: 16px;
    flex-direction: column;
    gap: 8px;
`;
const AiAskModalContainer = styled.div<{ $gap?: string }>`
    display: flex;
    padding: 4px 0px;
    align-items: start;
    gap: ${({ $gap }) => $gap || '20px'};
`;

function toUiSummaryItems(list?: Partial<UISummaryItem>[] | UISummaryItem[]): UISummaryItem[] {
    const safe = (list ?? []).filter(Boolean) as Partial<UISummaryItem>[];
    return safe.map((s) => ({
        ...(s as UISummaryItem),
        target: (s.target ?? '').trim(),
        englishTitle: (s.englishTitle ?? '').trim(),
        koreanTitle: (s.koreanTitle ?? '').trim(),
        description: (s.description ?? '').trim(),
    }));
}
function isVisibleSummaryItem(item: Partial<UISummaryItem> | undefined | null): item is UISummaryItem {
    const english = (item?.englishTitle ?? '').trim();
    const korean = (item?.koreanTitle ?? '').trim();
    const isKnownTitle = (english && english !== 'Unknown') || (korean && korean !== '알 수 없음');
    return Boolean(item && item.target && isKnownTitle && (item.description ?? '').trim().length > 0);
}
function sortByTargetPriority(list: UISummaryItem[] = []) {
    const priority: Record<string, number> = { 부모용: 1, 자녀용: 2, 부모보고용: 3 };
    return [...list].sort((a, b) => {
        const aPat2 = a.englishTitle?.includes('PAT-2');
        const bPat2 = b.englishTitle?.includes('PAT-2');
        if (aPat2 && !bPat2) return 1;
        if (!aPat2 && bPat2) return -1;
        const pa = priority[a.target] ?? 999;
        const pb = priority[b.target] ?? 999;
        if (pa !== pb) return pa - pb;
        const aPat = a.englishTitle?.includes('PAT');
        const bPat = b.englishTitle?.includes('PAT');
        return aPat === bPat ? 0 : aPat ? -1 : 1;
    });
}
